/**
 * Unified Permissions Hook
 *
 * Centralized RBAC logic for the entire application
 * Provides consistent permission checking across admin, workspace, and resource levels
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/features/auth/hooks/useAuth';

/**
 * Permission Types
 */
export type AdminRole = 'admin' | 'super_admin' | 'support' | null;
export type WorkspaceRole = 'owner' | 'admin' | 'member' | 'viewer';

/**
 * Permission Context
 */
export interface PermissionContext {
  // Admin permissions
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isSupport: boolean;
  canAccessAdmin: boolean;

  // Workspace permissions
  workspaceRole: WorkspaceRole | null;
  canManageWorkspace: boolean;
  canInviteMembers: boolean;
  canManageMembers: boolean;
  canViewWorkspace: boolean;

  // Resource permissions
  canCreateRepository: boolean;
  canDeleteRepository: boolean;
  canEditRepository: boolean;
  canViewRepository: boolean;

  // Governance permissions
  canViewGovernance: boolean;
  canManageGovernance: boolean;
  canViewAuditLogs: boolean;

  // Loading state
  isLoading: boolean;
}

/**
 * Current User Response Type
 */
interface CurrentUserResponse {
  user: {
    id: string;
    email: string;
    name: string;
    adminRole?: {
      role: AdminRole;
    } | null;
  };
}

/**
 * Workspace Member Response Type
 */
interface WorkspaceMemberResponse {
  userId: string;
  role: WorkspaceRole;
  workspaceId: string;
}

/**
 * Hook: usePermissions
 *
 * Provides unified permission checking for the current user
 *
 * @param workspaceId - Optional workspace ID for workspace-level permissions
 * @returns PermissionContext with all permission flags
 */
export function usePermissions(workspaceId?: string | null): PermissionContext {
  const { user: _user, isAuthenticated } = useAuth();

  // Fetch current user with admin role
  const { data: currentUser, isLoading: userLoading } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      try {
        return await api.get<CurrentUserResponse>('/auth/me');
      } catch {
        return null;
      }
    },
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch workspace member info if workspaceId provided
  const { data: workspaceMember, isLoading: workspaceLoading } = useQuery({
    queryKey: ['workspace-member', workspaceId, currentUser?.user?.id],
    queryFn: async () => {
      if (!workspaceId || !currentUser?.user?.id) return null;
      try {
        const response = await api.get<WorkspaceMemberResponse[]>(
          `/workspaces/${workspaceId}/members`
        );
        const members = Array.isArray(response) ? response : (response as { members?: WorkspaceMemberResponse[] })?.members || [];
        return members.find((m: WorkspaceMemberResponse) => m.userId === currentUser.user.id) || null;
      } catch {
        return null;
      }
    },
    enabled: !!workspaceId && !!currentUser?.user?.id,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Compute permissions
  const permissions = useMemo((): PermissionContext => {
    const adminRole = currentUser?.user?.adminRole?.role || null;
    const workspaceRole = workspaceMember?.role || null;

    // Admin permissions
    const isAdmin = adminRole === 'admin';
    const isSuperAdmin = adminRole === 'super_admin';
    const isSupport = adminRole === 'support';
    const canAccessAdmin = isAdmin || isSuperAdmin || isSupport;

    // Workspace permissions
    const canManageWorkspace = workspaceRole === 'owner' || workspaceRole === 'admin';
    const canInviteMembers = workspaceRole === 'owner' || workspaceRole === 'admin';
    const canManageMembers = workspaceRole === 'owner' || workspaceRole === 'admin';
    const canViewWorkspace = workspaceRole !== null;

    // Resource permissions (based on workspace role)
    const canCreateRepository = canManageWorkspace || workspaceRole === 'member';
    const canDeleteRepository = canManageWorkspace;
    const canEditRepository = canManageWorkspace || workspaceRole === 'member';
    const canViewRepository = canViewWorkspace;

    // Governance permissions
    const canViewGovernance = canViewWorkspace || canAccessAdmin;
    const canManageGovernance = canManageWorkspace || canAccessAdmin;
    const canViewAuditLogs = canAccessAdmin || canManageWorkspace;

    return {
      // Admin
      isAdmin,
      isSuperAdmin,
      isSupport,
      canAccessAdmin,

      // Workspace
      workspaceRole,
      canManageWorkspace,
      canInviteMembers,
      canManageMembers,
      canViewWorkspace,

      // Resources
      canCreateRepository,
      canDeleteRepository,
      canEditRepository,
      canViewRepository,

      // Governance
      canViewGovernance,
      canManageGovernance,
      canViewAuditLogs,

      // Loading
      isLoading: userLoading || workspaceLoading,
    };
  }, [currentUser, workspaceMember, userLoading, workspaceLoading]);

  return permissions;
}

/**
 * Hook: useAdminPermissions
 *
 * Convenience hook for admin-only permissions
 */
export function useAdminPermissions() {
  const permissions = usePermissions();
  return {
    isAdmin: permissions.isAdmin,
    isSuperAdmin: permissions.isSuperAdmin,
    isSupport: permissions.isSupport,
    canAccessAdmin: permissions.canAccessAdmin,
    isLoading: permissions.isLoading,
  };
}

/**
 * Hook: useWorkspacePermissions
 *
 * Convenience hook for workspace-level permissions
 *
 * @param workspaceId - Workspace ID
 */
export function useWorkspacePermissions(workspaceId: string | null) {
  const permissions = usePermissions(workspaceId);
  return {
    workspaceRole: permissions.workspaceRole,
    canManageWorkspace: permissions.canManageWorkspace,
    canInviteMembers: permissions.canInviteMembers,
    canManageMembers: permissions.canManageMembers,
    canViewWorkspace: permissions.canViewWorkspace,
    isLoading: permissions.isLoading,
  };
}
